/**
 * Chi può **aprire** questo pannello.
 *
 * ## Perché è un file a parte
 *
 * La decisione «questa persona lavora qui» è pura: un ruolo di piattaforma e un elenco
 * di permessi in ingresso, un `boolean` in uscita. Tenerla dentro `session-store` la
 * metterebbe in mezzo a token, refresh e revoche — cioè in un posto dove non si può
 * provare senza una rete finta — e questa è esattamente la riga che non deve sbagliare:
 * troppo larga fa entrare chi vedrà solo `403`, troppo stretta chiude fuori il titolare
 * dalla propria palestra.
 *
 * Nessun `import` da `@/`: il test la esegue con `node --test`, che risolve percorsi
 * relativi e non gli alias di Vite. Il tipo in ingresso è **strutturale** per la stessa
 * ragione, e non è una rinuncia — a questa funzione dei `Membership` interessa un campo.
 */

/**
 * I permessi che fanno di un'appartenenza un **posto di lavoro**.
 *
 * Serve a distinguere chi apre il pannello per lavorare da chi è semplicemente iscritto:
 * il ruolo `member` ha `organization:read` e `location:read`, cioè quanto basta a vedere
 * il nome della palestra e nient'altro. Lasciarlo entrare produrrebbe un pannello in cui
 * ogni schermata risponde `403` — peggio di un rifiuto, perché sembra un guasto.
 *
 * Ne basta **uno**: un allenatore che ha solo le schede e l'agenda ha comunque un
 * pannello che funziona, e chiedere di più escluderebbe i ruoli più ristretti — cioè
 * proprio quelli per cui una vista dedicata ha più valore.
 *
 * L'elenco è di **letture**. Un permesso di scrittura senza la lettura corrispondente non
 * esiste in `domain/permissions.ts`, tranne `member:invite`: chi può solo invitare non ha
 * un pannello da usare, ha un modulo — e infatti non compare qui.
 */
export const STAFF_PERMISSIONS = [
  'gym_member:read',
  'access:read',
  'sale:read',
  'card:read_any',
  'meal_plan:read',
  'appointment:read',
  'course:read',
  'product:read',
  'lead:read',
  'stats:read',
  'member:read',
] as const

/** Un'appartenenza con almeno un permesso da operatore. */
export function isStaffMembership(membership: { permissions?: string[] }): boolean {
  const granted = new Set(membership.permissions ?? [])
  return STAFF_PERMISSIONS.some((permission) => granted.has(permission))
}

/**
 * `true` se questa sessione può aprire il pannello.
 *
 * Due strade, e la seconda è nuova: l'amministratore di **piattaforma** (catalogo,
 * utenti, import) e chi lavora in una **palestra**. Fino a `draft.62` esisteva solo la
 * prima, ed era un residuo del pannello di quando l'unico perimetro era `/admin`:
 * significava che tutto ciò che è stato costruito da F2 in avanti — reception, cassa,
 * iscritti, schede, agenda, analisi — non era raggiungibile da nessuno dei mestieri per
 * cui è stato scritto. Un titolare non poteva vedere il proprio fatturato.
 *
 * `memberships` **assente** non è `memberships` vuoto: il primo è «non lo sappiamo»
 * (`/me` non ha risposto) e si ammette, il secondo è «non lavora in nessuna palestra» e
 * si rifiuta. Chiudere fuori chi ha credenziali valide per un errore di rete sarebbe il
 * guasto peggiore dei due, e chi entra senza permessi vede comunque solo le pagine che il
 * suo ruolo consente — la difesa è il `403` del server, non questa riga.
 */
export function canOpenPanel(input: {
  platformRole?: string | null
  memberships?: { permissions?: string[] }[] | undefined
}): boolean {
  if (input.platformRole === 'admin') return true
  if (input.memberships === undefined) return true
  return input.memberships.some(isStaffMembership)
}
